import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Gift, Share2, Trophy, Award, Sparkles, MessageCircle, Twitter, Linkedin, Mail, Users as UsersIcon, DollarSign, Loader2, Languages, Wallet, Clock, CheckCircle2, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

interface ReferralMe {
  code: string;
  share_link: string;
  share_card: "product" | "testimonial" | "trial";
  signups_count: number;
  paid_conversions: number;
  free_days_earned: number;
  cash_tier_unlocked: boolean;
  cash_tier_progress_pct: number;
  cash_tier_threshold: number;
  commission_pending_cents: number;
  leaderboard_opt_in: boolean;
  reward_terms: {
    free_days_per_paid_conversion: number;
    invite_bonus_days: number;
    cash_commission_pct: number;
    cash_commission_months: number;
    cash_tier_unlock_at: number;
    commission_hold_days?: number;
  };
}
interface LeaderboardRow { rank: number; display_name: string; paid_conversions: number }

interface PayoutTimelineEntry {
  kind: "queued" | "dispatched" | "held";
  amount_cents: number;
  currency: string;
  at: string;
  method?: string | null;
  reference?: string | null;
  status?: string | null;
  clears_at?: string | null;
}
interface PayoutTimeline {
  items: PayoutTimelineEntry[];
  total_dispatched_cents: number;
  total_queued_cents: number;
  total_held_cents: number;
  currency: string;
  hold_days: number;
}

const fmtTimelineMoney = (cents: number, currency: string): string => {
  const c = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(c === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: c,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `${c} ${(cents / 100).toFixed(0)}`;
  }
};
const fmtTimelineDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
};

interface CardAnalyticsRow {
  card: "product" | "testimonial" | "trial";
  impressions: number;
  bot_impressions: number;
  user_impressions: number;
  signups: number;
  paid: number;
  signup_rate: number;
  paid_rate: number;
  is_winner: boolean;
}
interface CardAnalytics {
  rows: CardAnalyticsRow[];
  winner: "product" | "testimonial" | "trial" | null;
  total_signups: number;
}

const CARD_LABELS: Record<string, string> = {
  product: "Product angle",
  testimonial: "Testimonial",
  trial: "14 free days",
};

const Referrals: React.FC = () => {
  const qc = useQueryClient();
  const me = useQuery<ReferralMe>({
    queryKey: ["referrals-me"],
    queryFn: async () => (await api.get("/referrals/me")).data,
  });
  const board = useQuery<LeaderboardRow[]>({
    queryKey: ["referrals-leaderboard"],
    queryFn: async () => (await api.get("/referrals/leaderboard")).data,
  });
  const timeline = useQuery<PayoutTimeline>({
    queryKey: ["referrals-payouts-timeline"],
    queryFn: async () => (await api.get("/referrals/payouts-timeline")).data,
  });
  const cardStats = useQuery<CardAnalytics>({
    queryKey: ["referrals-card-analytics"],
    queryFn: async () => (await api.get("/referrals/card-analytics")).data,
    // Analytics don't change every second — refresh once a minute.
    refetchInterval: 60_000,
  });
  const optIn = useMutation({
    mutationFn: async (v: boolean) => (await api.post("/referrals/leaderboard-opt-in", { opt_in: v })).data,
    onSuccess: () => {
      toast.success("Preference saved");
      qc.invalidateQueries({ queryKey: ["referrals-me"] });
      qc.invalidateQueries({ queryKey: ["referrals-leaderboard"] });
    },
  });

  const setCard = useMutation({
    mutationFn: async (card: "product" | "testimonial" | "trial") =>
      (await api.post("/referrals/share-card", { card })).data,
    onSuccess: () => {
      toast.success("Preview updated — your next share will show the new card");
      qc.invalidateQueries({ queryKey: ["referrals-me"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const [copied, setCopied] = useState<"code" | "link" | "message" | null>(null);
  const copy = (kind: "code" | "link" | "message", value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(kind);
      toast.success("Copied");
      setTimeout(() => setCopied(null), 1500);
    });
  };

  // ----------------- Localized invite copy -----------------
  // Referrers can share in their audience's language. Values interpolate
  // `{days}` (bonus trial days) and `{link}` (their share URL).
  const LOCALES = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "pt", label: "Português", flag: "🇧🇷" },
  ] as const;

  const TEMPLATES: Record<string, string> = {
    en: "I've been using SociBaba to turn every social comment into a lead — the AI reply engine is a game-changer. Get {days} bonus trial days when you sign up with my link: {link}",
    hi: "मैं SociBaba इस्तेमाल कर रहा/रही हूँ — इससे हर सोशल कमेंट लीड बन जाता है, और AI reply engine कमाल का है। मेरे लिंक से साइनअप करने पर आपको {days} एक्स्ट्रा ट्रायल दिन मिलेंगे: {link}",
    es: "Estoy usando SociBaba para convertir cada comentario social en un lead — el motor de respuestas con IA es increíble. Regístrate con mi enlace y obtén {days} días extra de prueba: {link}",
    pt: "Estou usando o SociBaba para transformar cada comentário social em um lead — o motor de respostas com IA é sensacional. Cadastre-se pelo meu link e ganhe {days} dias extras de teste: {link}",
  };

  const EMAIL_SUBJECTS: Record<string, string> = {
    en: "SociBaba — free trial + bonus days",
    hi: "SociBaba — फ्री ट्रायल + बोनस दिन",
    es: "SociBaba — prueba gratis + días extra",
    pt: "SociBaba — teste grátis + dias bônus",
  };

  const [locale, setLocale] = useState<string>(() => {
    // Auto-detect from browser once, then persist to localStorage.
    try {
      const saved = window.localStorage.getItem("sb.referrals.locale");
      if (saved && TEMPLATES[saved]) return saved;
      const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
      return TEMPLATES[nav] ? nav : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try { window.localStorage.setItem("sb.referrals.locale", locale); } catch { /* noop */ }
  }, [locale]);

  const shareMsg = useMemo(() => {
    const link = me.data?.share_link || "";
    const days = String(me.data?.reward_terms.invite_bonus_days ?? 14);
    const tpl = TEMPLATES[locale] || TEMPLATES.en;
    return tpl.replace("{days}", days).replace("{link}", link);
  }, [me.data, locale]);

  const [customMsg, setCustomMsg] = useState<string>("");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    // Reset the customized copy whenever locale switches so users see the
    // fresh template — but keep the field intact once they've edited.
    if (!editing) setCustomMsg(shareMsg);
  }, [shareMsg, editing]);

  const share = (channel: "whatsapp" | "twitter" | "linkedin" | "email") => {
    if (!me.data) return;
    const enc = encodeURIComponent;
    const link = me.data.share_link;
    const msg = (customMsg || shareMsg);
    const subject = EMAIL_SUBJECTS[locale] || EMAIL_SUBJECTS.en;
    const urls = {
      whatsapp: `https://wa.me/?text=${enc(msg)}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc(msg)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(link)}`,
      email:    `mailto:?subject=${enc(subject)}&body=${enc(msg)}`,
    } as const;
    window.open(urls[channel], "_blank", "noopener,noreferrer");
  };

  const shell = (children: React.ReactNode) => (
    <DashboardLayout
      title="Referrals"
      subtitle="Turn your audience into your growth engine — earn free Pro days on every paid signup."
      seoPath="/referrals"
      seoDescription="SociBaba referral program — earn free Pro days and unlock cash commissions when your invites subscribe."
    >
      {children}
    </DashboardLayout>
  );

  if (me.isLoading || !me.data) return shell(<div className="sg-skeleton h-64" data-testid="referrals-skeleton" />);
  const d = me.data;
  const commissionUsd = (d.commission_pending_cents / 100).toFixed(2);
  const isDollar = d.commission_pending_cents > 0; // simplistic — currency taken from event
  return shell(
    <div className="space-y-6" data-testid="referrals-page">
      {/* Hero — code + share */}
      <div className="sg-card p-6 md:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-[color:var(--sb-saffron)]/10 text-[color:var(--sb-saffron)] flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-xl font-bold">Your referral code</div>
            <div className="text-xs text-[color:var(--sg-muted)]">Every paid signup = {d.reward_terms.free_days_per_paid_conversion} free Pro days for you + {d.reward_terms.invite_bonus_days} bonus trial days for them.</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-dashed border-[color:var(--sg-border)] p-3 bg-[color:var(--sg-muted-bg)]/40">
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">Code</span>
            <span className="font-mono text-lg font-bold tracking-widest flex-1" data-testid="referrals-code">{d.code}</span>
            <Button size="sm" variant="outline" onClick={() => copy("code", d.code)} data-testid="referrals-copy-code">
              <Copy className="h-3.5 w-3.5 mr-1" /> {copied === "code" ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-[color:var(--sg-border)] p-3">
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">Link</span>
            <span className="text-xs truncate flex-1" data-testid="referrals-link">{d.share_link}</span>
            <Button size="sm" variant="outline" onClick={() => copy("link", d.share_link)} data-testid="referrals-copy-link">
              <Copy className="h-3.5 w-3.5 mr-1" /> {copied === "link" ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Languages className="h-3.5 w-3.5 text-[color:var(--sg-muted)]" />
          <span className="text-xs text-[color:var(--sg-muted)] mr-1">Invite in</span>
          {LOCALES.map((l) => (
            <button
              key={l.code}
              onClick={() => { setEditing(false); setLocale(l.code); }}
              data-testid={`referrals-locale-${l.code}`}
              className={`px-2.5 py-1 rounded-full border text-xs transition ${
                locale === l.code
                  ? "border-[color:var(--sb-saffron)] bg-[color:var(--sb-saffron)]/10 text-[color:var(--sb-saffron)] font-semibold"
                  : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:border-[color:var(--sg-fg)]"
              }`}
            >
              <span className="mr-1">{l.flag}</span>{l.label}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <Textarea
            value={customMsg}
            onChange={(e) => { setEditing(true); setCustomMsg(e.target.value); }}
            rows={3}
            className="text-sm"
            data-testid="referrals-share-message"
          />
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-[color:var(--sg-muted)]">
              {editing ? "Personalised — reset to switch back to the template." : "Auto-filled from template. Edit freely."}
            </span>
            <div className="flex items-center gap-2">
              {editing && (
                <button
                  onClick={() => { setEditing(false); setCustomMsg(shareMsg); }}
                  className="underline text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                  data-testid="referrals-message-reset"
                >
                  Reset
                </button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy("message", customMsg || shareMsg)}
                data-testid="referrals-copy-message"
              >
                <Copy className="h-3.5 w-3.5 mr-1" />
                {copied === "message" ? "Copied" : "Copy message"}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-3.5 w-3.5 text-[color:var(--sg-muted)]" />
            <span className="text-xs text-[color:var(--sg-muted)]">Preview card that unfurls on WhatsApp / X</span>
          </div>
          {/* Winner nudge — saffron ribbon above the card picker */}
          {cardStats.data?.winner && (() => {
            const winnerRow = cardStats.data!.rows.find((r) => r.card === cardStats.data!.winner)!;
            const isMine = d.share_card === cardStats.data!.winner;
            const paidPct = Math.round((winnerRow.paid_rate || 0) * 100);
            const highConfidence = winnerRow.signups >= 3 && winnerRow.paid > 0;
            const winnerLabel = CARD_LABELS[cardStats.data!.winner!];
            return (
              <div
                data-testid="referrals-card-winner-banner"
                className={`mb-3 rounded-lg border overflow-hidden ${
                  isMine
                    ? "border-emerald-200 bg-emerald-50/70"
                    : "border-[color:var(--sb-saffron)]/40 bg-gradient-to-r from-[#fff7ed] to-[#fef3c7]"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 p-3">
                  <div
                    className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isMine ? "bg-emerald-500 text-white" : "bg-[color:var(--sb-saffron)] text-white"
                    }`}
                  >
                    {isMine ? <CheckCircle2 className="h-4 w-4" /> : <Crown className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[color:var(--sg-fg)]">
                      {isMine ? (
                        highConfidence ? (
                          <>You're already using the top-converting card 🎉</>
                        ) : (
                          <>You're using the card that's getting the most traction 🎯</>
                        )
                      ) : highConfidence ? (
                        <>🏆 <b>{winnerLabel}</b> is converting best right now</>
                      ) : (
                        <>🌱 <b>{winnerLabel}</b> is getting the most traction so far</>
                      )}
                    </div>
                    <div className="text-[11.5px] text-[color:var(--sg-muted)] mt-0.5">
                      {winnerRow.signups} signup{winnerRow.signups === 1 ? "" : "s"}
                      {winnerRow.paid > 0 && <> · {winnerRow.paid} paid</>}
                      {paidPct > 0 && <> · <b>{paidPct}%</b> paid-conversion rate</>}
                      {" "}across the community
                      {!highConfidence && <> — data is still early, take this as a hint</>}.
                    </div>
                  </div>
                  {!isMine && (
                    <Button
                      size="sm"
                      onClick={() => setCard.mutate(cardStats.data!.winner!)}
                      disabled={setCard.isPending}
                      data-testid="referrals-card-winner-switch"
                      className="bg-[color:var(--sb-saffron)] hover:brightness-95 text-white h-8 shrink-0"
                    >
                      {setCard.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                      Use this card
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="grid grid-cols-3 gap-2">
            {([
              { key: "product", label: "Product angle" },
              { key: "testimonial", label: "Testimonial" },
              { key: "trial", label: "14 free days" },
            ] as const).map((opt) => {
              const active = d.share_card === opt.key;
              const src = `${api.defaults.baseURL}/static/og/${opt.key}.png`;
              const row = cardStats.data?.rows.find((r) => r.card === opt.key);
              const isWinner = cardStats.data?.winner === opt.key;
              const paidPct = row && row.paid_rate ? Math.round(row.paid_rate * 100) : 0;
              return (
                <button
                  key={opt.key}
                  onClick={() => setCard.mutate(opt.key)}
                  disabled={setCard.isPending}
                  data-testid={`referrals-card-${opt.key}`}
                  className={`relative text-left border rounded-lg overflow-hidden bg-white transition ${
                    active
                      ? "border-[color:var(--sb-saffron)] ring-2 ring-[color:var(--sb-saffron)]/40 shadow-sm"
                      : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)]"
                  }`}
                >
                  {isWinner && (
                    <span
                      data-testid={`referrals-card-${opt.key}-winner`}
                      className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-[color:var(--sb-saffron)] text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 shadow-sm"
                    >
                      <Crown className="h-2.5 w-2.5" /> Top
                    </span>
                  )}
                  <div className="aspect-[1200/630] w-full bg-[#fffaf5]">
                    <img
                      src={src}
                      alt={opt.label}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="px-2 py-1.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium truncate">{opt.label}</span>
                    {active ? (
                      <span className="text-[10px] text-[color:var(--sb-saffron)] font-semibold shrink-0">
                        SELECTED
                      </span>
                    ) : row && row.signups > 0 ? (
                      <span
                        className="text-[10px] text-[color:var(--sg-muted)] font-medium shrink-0 tabular-nums"
                        title={`${row.signups} signup(s) · ${row.paid} paid`}
                      >
                        {paidPct > 0 ? `${paidPct}%` : `${row.signups}✕`}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[color:var(--sg-muted)] mr-1"><Share2 className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />Share on</span>
          <Button size="sm" variant="outline" onClick={() => share("whatsapp")} data-testid="referrals-share-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp</Button>
          <Button size="sm" variant="outline" onClick={() => share("twitter")} data-testid="referrals-share-twitter"><Twitter className="h-3.5 w-3.5 mr-1" />X / Twitter</Button>
          <Button size="sm" variant="outline" onClick={() => share("linkedin")} data-testid="referrals-share-linkedin"><Linkedin className="h-3.5 w-3.5 mr-1" />LinkedIn</Button>
          <Button size="sm" variant="outline" onClick={() => share("email")} data-testid="referrals-share-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testId="referrals-stat-signups" icon={UsersIcon} label="Signups" value={d.signups_count} />
        <StatCard testId="referrals-stat-paid" icon={Award} label="Paid conversions" value={d.paid_conversions} />
        <StatCard testId="referrals-stat-days" icon={Sparkles} label="Free days earned" value={d.free_days_earned} suffix=" days" />
        <StatCard testId="referrals-stat-commission" icon={DollarSign} label="Commission pending" value={`$${commissionUsd}`} highlight={isDollar} />
      </div>

      {/* Cash tier */}
      <div className="sg-card p-5" data-testid="referrals-cash-tier">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Trophy className={`h-4 w-4 ${d.cash_tier_unlocked ? "text-[color:var(--sb-saffron)]" : "text-[color:var(--sg-muted)]"}`} />
            <div className="font-semibold">
              {d.cash_tier_unlocked
                ? "Cash tier unlocked"
                : `${d.paid_conversions}/${d.cash_tier_threshold} paid signups to unlock cash`}
            </div>
          </div>
          <div className="text-xs text-[color:var(--sg-muted)]">
            {d.reward_terms.cash_commission_pct}% recurring commission for {d.reward_terms.cash_commission_months} months
          </div>
        </div>
        <div className="h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
          <div
            className="h-full bg-[color:var(--sb-saffron)] transition-all"
            style={{ width: `${d.cash_tier_progress_pct}%` }}
            data-testid="referrals-cash-tier-progress"
          />
        </div>
        {d.cash_tier_unlocked && (
          <div className="mt-3 text-xs text-[color:var(--sg-muted)]" data-testid="referrals-cash-tier-note">
            You've unlocked cash payouts. Email <a href="mailto:support@socibaba.com" className="underline text-[color:var(--sb-saffron)]">support@socibaba.com</a> with your KYC details to receive your accrued balance.
          </div>
        )}
      </div>

      {/* Cash timeline */}
      {timeline.data && (timeline.data.items.length > 0 ||
                          timeline.data.total_queued_cents > 0 ||
                          timeline.data.total_dispatched_cents > 0 ||
                          timeline.data.total_held_cents > 0) && (
        <div className="sg-card p-5" data-testid="referrals-timeline-card">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[color:var(--sb-saffron)]" />
              <div className="font-semibold">Your cash timeline</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <span className="text-[color:var(--sg-muted)]">Held</span>{" "}
                <span className="font-semibold text-amber-700" data-testid="timeline-total-held">
                  {fmtTimelineMoney(timeline.data.total_held_cents, timeline.data.currency)}
                </span>
              </div>
              <div>
                <span className="text-[color:var(--sg-muted)]">Queued</span>{" "}
                <span className="font-semibold" data-testid="timeline-total-queued">
                  {fmtTimelineMoney(timeline.data.total_queued_cents, timeline.data.currency)}
                </span>
              </div>
              <div>
                <span className="text-[color:var(--sg-muted)]">Paid out</span>{" "}
                <span className="font-semibold text-emerald-700" data-testid="timeline-total-dispatched">
                  {fmtTimelineMoney(timeline.data.total_dispatched_cents, timeline.data.currency)}
                </span>
              </div>
            </div>
          </div>
          {timeline.data.total_held_cents > 0 && (
            <div className="mb-3 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="timeline-hold-banner">
              Commission is held for <b>{timeline.data.hold_days} days</b> after each buyer's first payment to safeguard against refunds/cancellations. It becomes available for payout after that cooling window.
            </div>
          )}
          {timeline.data.items.length === 0 ? (
            <div className="text-sm text-[color:var(--sg-muted)]" data-testid="timeline-empty">
              No payouts recorded yet. Verified referrers with balance above the threshold see queued transfers appear here in real time.
            </div>
          ) : (
            <ol className="relative border-l-2 border-[color:var(--sg-border)] ml-1 space-y-4">
              {timeline.data.items.slice(0, 20).map((it, idx) => {
                const isDispatched = it.kind === "dispatched";
                const isHeld = it.kind === "held";
                const dotColor = isDispatched
                  ? "bg-emerald-100 text-emerald-700"
                  : isHeld
                    ? "bg-amber-100 text-amber-700"
                    : "bg-amber-100 text-amber-700";
                const badgeColor = isDispatched
                  ? "text-emerald-700"
                  : isHeld
                    ? "text-amber-700"
                    : "text-amber-700";
                const badge = isDispatched ? "Paid" : isHeld ? "Held" : "Queued";
                return (
                  <li key={idx} className="pl-4 relative" data-testid={`timeline-entry-${idx}`}>
                    <span className={`absolute -left-[9px] top-0.5 h-4 w-4 rounded-full flex items-center justify-center ${dotColor}`}>
                      {isDispatched ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    </span>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold">
                          {fmtTimelineMoney(it.amount_cents, it.currency)}{" "}
                          <span className={`ml-1 text-[10px] font-semibold uppercase tracking-wider ${badgeColor}`}>
                            {badge}
                          </span>
                        </div>
                        <div className="text-xs text-[color:var(--sg-muted)]">
                          {isHeld
                            ? `Buyer paid ${fmtTimelineDate(it.at)}${it.clears_at ? ` · Clears ${fmtTimelineDate(it.clears_at)}` : ""}`
                            : fmtTimelineDate(it.at)}
                          {it.method ? ` · ${it.method}` : ""}
                          {it.reference ? ` · Ref ${it.reference}` : ""}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {/* Opt-in + Leaderboard */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="sg-card p-5" data-testid="referrals-optin-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold">Appear on the public leaderboard</div>
              <div className="text-xs text-[color:var(--sg-muted)] mt-1">Optional social proof — only your first name and paid-signup count are shown.</div>
            </div>
            <Switch
              checked={d.leaderboard_opt_in}
              onCheckedChange={(v) => optIn.mutate(!!v)}
              data-testid="referrals-optin-toggle"
            />
          </div>
        </div>
        <div className="sg-card p-5" data-testid="referrals-leaderboard-card">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-[color:var(--sb-saffron)]" />
            <div className="font-semibold">Top referrers</div>
          </div>
          {board.isLoading ? (
            <div className="sg-skeleton h-32" />
          ) : (board.data || []).length === 0 ? (
            <div className="text-xs text-[color:var(--sg-muted)] py-6 text-center">
              Be the first — flip the switch and get onto the board.
            </div>
          ) : (
            <ol className="space-y-1.5">
              {(board.data || []).map((row) => (
                <li key={row.rank} data-testid={`referrals-leaderboard-row-${row.rank}`} className="flex items-center gap-3 text-sm">
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${row.rank <= 3 ? "bg-[color:var(--sb-saffron)] text-white" : "bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)]"}`}>
                    {row.rank}
                  </span>
                  <span className="flex-1 truncate">{row.display_name}</span>
                  <span className="text-xs text-[color:var(--sg-muted)]">{row.paid_conversions} paid</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

interface StatProps { testId: string; icon: any; label: string; value: React.ReactNode; suffix?: string; highlight?: boolean }
const StatCard: React.FC<StatProps> = ({ testId, icon: Icon, label, value, suffix, highlight }) => (
  <div className="sg-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] font-semibold">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className={`mt-1 font-display text-2xl font-bold ${highlight ? "text-[color:var(--sb-saffron)]" : ""}`}>
      {value}{suffix}
    </div>
  </div>
);

export default Referrals;
